import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '../lib/cn.js';
import { createOverlayPrimitive } from './overlay.js';
/**
 * Side-drawer built on Radix Dialog. Slides in from the given edge. Defaults
 * to "left" because the common case (mobile nav) opens from the left. Width
 * is capped at `min(18rem, 85vw)` so it doesn't eat the whole screen on
 * tiny viewports.
 *
 * Namespace-style API mirrors `Dialog`, `DropdownMenu`, etc. so callers see a
 * consistent shape: `Sheet.Root`, `Sheet.Trigger`, `Sheet.Content`, `Sheet.Close`.
 */
const SheetRoot = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetClose = DialogPrimitive.Close;
const SheetPortal = DialogPrimitive.Portal;
const SheetOverlay = createOverlayPrimitive(DialogPrimitive.Overlay, 'sheet', 'Sheet.Overlay');
const sideToClasses = {
    left: 'inset-y-0 left-0 h-full w-[min(18rem,85vw)] border-r',
    right: 'inset-y-0 right-0 h-full w-[min(18rem,85vw)] border-l',
    top: 'inset-x-0 top-0 h-auto max-h-[85dvh] w-full border-b',
    bottom: 'inset-x-0 bottom-0 h-auto max-h-[85dvh] w-full border-t',
};
// Sheets render in a Radix Portal at the document root, so they escape the
// app-shell's `env(safe-area-inset-*)` padding and must reserve the notch /
// home-indicator space themselves. We pad on the inset edges relevant to each
// side: a left/right drawer spans the full height so it needs top + bottom (and
// its own near edge); a top/bottom sheet spans the full width so it needs left +
// right (and its own near edge).
const sideToSafeAreaStyle = {
    left: {
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
    },
    right: {
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingRight: 'env(safe-area-inset-right)',
    },
    top: {
        paddingTop: 'env(safe-area-inset-top)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
    },
    bottom: {
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
    },
};
const SheetContent = React.forwardRef(({ className, side = 'left', style, children, ...props }, ref) => (_jsxs(SheetPortal, { children: [_jsx(SheetOverlay, {}), _jsx(DialogPrimitive.Content, { ref: ref, className: cn('fixed z-50 flex flex-col gap-0 overflow-y-auto border-[var(--color-border-default)] bg-[var(--color-surface-raised)] shadow-xl', sideToClasses[side], className), style: { ...sideToSafeAreaStyle[side], ...style }, ...props, children: children })] })));
SheetContent.displayName = 'Sheet.Content';
/**
 * Заголовок панели для чтения с экрана: Radix требует `DialogTitle` у каждого
 * диалога (иначе ошибка в консоли). Видимый заголовок не нужен — `sr-only`
 * по умолчанию.
 */
const SheetTitle = React.forwardRef(({ className, ...props }, ref) => (_jsx(DialogPrimitive.Title, { ref: ref, className: cn('sr-only', className), ...props })));
SheetTitle.displayName = 'Sheet.Title';
export const Sheet = {
    Root: SheetRoot,
    Trigger: SheetTrigger,
    Close: SheetClose,
    Content: SheetContent,
    Overlay: SheetOverlay,
    Title: SheetTitle,
};
//# sourceMappingURL=sheet.js.map